/**
 * Numeric problem input.
 */
import React, { useEffect, useState } from "react";
import { TextField } from "@mui/material";

import CorrectAnswerCard from "./CorrectAnswerCard";
import WrongAnswerCard from "./WrongAnswerCard";
import RenderResponsePrompt from "./RenderResponsePrompt";

/**
 * Numeric input that grades against the correct value and optional tolerance.
 *
 * @param {{ tag: Element, onSetAnswer?: Function }} props
 */
export default function NumericalResponseRender(props) {
  const { tag, grade, onSetAnswer, isShowMark, setShowMark } = props;

  const [inputValue, setInputValue] = useState("");
  const [tolerance, setTolerance] = useState({});
  const [correctAnswer, setCorrectAnswer] = useState(null);
  const [isCorrect, setIsCorrect] = useState(false);

  useEffect(() => {
    if (!tag) return;

    setCorrectAnswer(parseFloat(tag.getAttribute("answer")));

    const param = tag.querySelector("responseparam");
    if (param && param.getAttribute("type") === "tolerance") {
      const val = param.getAttribute("default") || "";
      setTolerance(
        val.includes("%")
          ? { type: "percent", value: parseFloat(val.replaceAll("%", "")) }
          : { type: "range", value: parseFloat(val) }
      );
    } else {
      setTolerance({});
    }
  }, [tag]);

  /**
   * Returns true when the number is within the configured tolerance.
   *
   * @param {number} valNum Learner value.
   * @returns {boolean}
   */
  const evalCorrect = (valNum) => {
    if (Number.isNaN(valNum) || correctAnswer == null) return false;

    if (Object.keys(tolerance).length) {
      if (tolerance.type === "range" && Math.abs(valNum - correctAnswer) < tolerance.value) return true;
      if (tolerance.type === "percent" && Math.abs(100.0 - (100.0 * valNum) / correctAnswer) < tolerance.value) return true;
      return false;
    }
    return valNum === correctAnswer;
  };

  /**
   * Parses the typed number, grades it, and reports the answer.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e Change event.
   * @returns {void}
   */
  const onChange = (e) => {
    setShowMark(false);

    const raw = e.target.value; // string
    setInputValue(raw);

    const valNum = raw === "" ? NaN : parseFloat(raw);
    const correct = evalCorrect(valNum);

    onSetAnswer({
      id: grade?.id,
      student_answer: raw,
      correctness: correct ? "correct" : "incorrect",
    });

    setIsCorrect(correct);
  };

  return (
    <div className="mb-2">
      <RenderResponsePrompt tag={tag} />

      <TextField
        size="small"
        type="number"
        value={inputValue}
        onChange={onChange}
        inputProps={{ step: "any" }}
        variant="outlined"
      />

      {isShowMark && (isCorrect ? <CorrectAnswerCard /> : <WrongAnswerCard />)}
    </div>
  );
}
